import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "react-toastify";

const CatalogueModals = ({
  showResetConfirmModal,
  setShowResetConfirmModal,
  resettingStock,
  handleResetStock,
  showCustomizationModal,
  setShowCustomizationModal,
  customizationSets,
  selectedCustomizations,
  setSelectedCustomizations,
  handleApplyCustomization,
  handleInsertFoodCustomization,
}) => {
  const [showAddCustomization, setShowAddCustomization] = useState(false);
  const [customizeField, setCustomizeField] = useState("");
  const [optionInput, setOptionInput] = useState("");
  const [newOptions, setNewOptions] = useState([]);
  const [savingCustomization, setSavingCustomization] = useState(false);

  const resetAddCustomizationForm = () => {
    setCustomizeField("");
    setOptionInput("");
    setNewOptions([]);
    setShowAddCustomization(false);
  };

  const handleCloseCustomizationModal = () => {
    setShowCustomizationModal(false);
    resetAddCustomizationForm();
  };

  const handleAddOption = () => {
    const value = String(optionInput || "").trim();

    if (!value) return;

    const alreadyExists = newOptions.some(
      (option) => option.toLowerCase() === value.toLowerCase(),
    );

    if (alreadyExists) {
      toast.error("This option already exists");
      return;
    }

    setNewOptions((prev) => [...prev, value]);
    setOptionInput("");
  };

  const handleOptionKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddOption();
    }
  };

  const handleRemoveOption = (index) => {
    setNewOptions((prev) =>
      prev.filter((_, optionIndex) => optionIndex !== index),
    );
  };

  const handleSaveNewCustomization = async () => {
    const field = String(customizeField || "").trim();

    if (!field) {
      toast.error("Please enter customization name");
      return;
    }

    if (!newOptions.length) {
      toast.error("Please add at least one option");
      return;
    }

    if (typeof handleInsertFoodCustomization !== "function") {
      toast.error("Customization API is not configured");
      return;
    }

    try {
      setSavingCustomization(true);

      const success = await handleInsertFoodCustomization({
        customizeField: field,
        options: newOptions,
      });

      if (success) {
        resetAddCustomizationForm();
      }
    } catch (error) {
      console.error("Save customization error:", error);
      toast.error("Failed to save customization");
    } finally {
      setSavingCustomization(false);
    }
  };

  return (
    <>
      {/* RESET MODAL */}
      {showResetConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b px-6 py-5">
              <h2 className="text-lg font-semibold text-gray-900">
                Confirm Stock Reset
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                This will reset stock for all catalogue items. This action
                cannot be undone.
              </p>
            </div>

            <div className="px-6 py-5">
              <div className="rounded-xl border border-red-100 bg-red-50 p-4">
                <p className="text-sm font-medium text-red-700">
                  Are you sure you want to reset stock?
                </p>

                <p className="mt-1 text-xs text-red-500">
                  Click confirm only if you really want to reset all stock
                  values.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowResetConfirmModal(false)}
                disabled={resettingStock}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleResetStock}
                disabled={resettingStock}
                className={`rounded-lg px-4 py-2 text-sm text-white ${
                  resettingStock
                    ? "cursor-not-allowed bg-red-300"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {resettingStock ? "Resetting..." : "Yes, Reset Stock"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMIZATION MODAL */}
      {showCustomizationModal && (
        <div className="montserrat-font-1 fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between bg-emerald-500 px-6 py-4 text-white">
              <h2 className="text-lg font-semibold">Customize Category</h2>

              <button
                type="button"
                onClick={handleCloseCustomizationModal}
                className="text-xl text-white"
              >
                ✕
              </button>
            </div>

            <div className="montserrat-font-2 flex items-center justify-between gap-3 border-b px-6 py-4">
              <p className="text-sm text-gray-600">
                Select customization preferences to define how this category
                behaves in orders.
              </p>

              <button
                type="button"
                onClick={() => setShowAddCustomization((prev) => !prev)}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-600"
              >
                {showAddCustomization ? <X size={15} /> : <Plus size={15} />}

                {showAddCustomization ? "Cancel" : "Add New"}
              </button>
            </div>

            {showAddCustomization && (
              <div className="border-b bg-emerald-50/50 px-6 py-4">
                <div className="rounded-xl border border-emerald-100 bg-white p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-800">
                    Add New Customization
                  </h3>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">
                        Customization Name
                      </label>

                      <input
                        type="text"
                        value={customizeField}
                        onChange={(event) =>
                          setCustomizeField(event.target.value)
                        }
                        placeholder="e.g. Sweetness"
                        className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none transition focus:border-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">
                        Options
                      </label>

                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={optionInput}
                          onChange={(event) =>
                            setOptionInput(event.target.value)
                          }
                          onKeyDown={handleOptionKeyDown}
                          placeholder="e.g. Less Sweet"
                          className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-sm outline-none transition focus:border-emerald-500"
                        />

                        <button
                          type="button"
                          onClick={handleAddOption}
                          className="h-10 rounded-lg border border-emerald-500 px-3 text-sm font-medium text-emerald-600 hover:bg-emerald-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {newOptions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {newOptions.map((option, index) => (
                        <span
                          key={`${option}-${index}`}
                          className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700"
                        >
                          {option}

                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="ml-1 text-emerald-700 hover:text-red-500"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSaveNewCustomization}
                      disabled={savingCustomization}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                        savingCustomization
                          ? "cursor-not-allowed bg-emerald-300"
                          : "bg-emerald-500 hover:bg-emerald-600"
                      }`}
                    >
                      {savingCustomization ? "Saving..." : "Save Customization"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto px-6 py-4 sm:grid-cols-2">
              {Array.isArray(customizationSets) &&
              customizationSets.length > 0 ? (
                customizationSets.map((set) => {
                  const field = String(set?.customize_field || "").trim();

                  const options = Array.isArray(set?.options)
                    ? set.options
                    : [];

                  const customizationId = String(set.pk_customization_id);

                  const isSelected =
                    selectedCustomizations.includes(customizationId);

                  const toggleCustomization = () => {
                    setSelectedCustomizations((prev) =>
                      prev.includes(customizationId)
                        ? prev.filter((id) => id !== customizationId)
                        : [...prev, customizationId],
                    );
                  };

                  return (
                    <div
                      key={set.pk_customization_id}
                      onClick={toggleCustomization}
                      className={`border rounded-xl p-4 shadow-sm cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "border-emerald-500 bg-emerald-50"
                          : "hover:border-gray-400"
                      }`}
                    >
                      <div className="montserrat-font-1 mb-3 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="cursor-pointer accent-emerald-500"
                        />

                        <span className="font-medium text-gray-800">
                          {field}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {options.map((option, index) => (
                          <span
                            key={`${option}-${index}`}
                            className="rounded-full bg-emerald-500 px-3 py-1 text-xs text-white"
                          >
                            {option}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-10 text-center text-sm text-gray-500">
                  No customizations found.
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t px-6 py-4">
              <button
                type="button"
                onClick={handleCloseCustomizationModal}
                className="rounded-full border border-emerald-500 px-5 py-2 text-emerald-600 hover:bg-emerald-50"
              >
                Close
              </button>

              <button
                type="button"
                onClick={handleApplyCustomization}
                disabled={!selectedCustomizations.length}
                className={`rounded-full px-6 py-2 text-white ${
                  selectedCustomizations.length
                    ? "bg-emerald-500 hover:bg-emerald-600"
                    : "cursor-not-allowed bg-emerald-300"
                }`}
              >
                Apply Customization
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CatalogueModals;
